/**
 * Compact plan/trial indicator that lives in the existing top bar, next to the
 * user name — deliberately not a second bar of its own.
 *
 * It carries only the one fact that is time-critical right now; the numbers,
 * dates and meters live on Settings > Billing. The full-width
 * <SubscriptionBanner> is reserved for the single state loud enough to need it:
 * moderation has actually stopped.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Ban, Clock } from "lucide-react";
import { useSubscription } from "../contexts/SubscriptionContext";
import { getSubscriptionState, Severity } from "../lib/subscriptionState";

const STYLES: Record<Severity, { chip: string; icon: React.ElementType }> = {
  info: {
    chip: "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100",
    icon: Clock,
  },
  warning: {
    chip: "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100",
    icon: AlertTriangle,
  },
  severe: {
    chip: "bg-orange-50 text-orange-800 border-orange-200 hover:bg-orange-100",
    icon: AlertTriangle,
  },
  danger: {
    chip: "bg-red-50 text-red-700 border-red-200 hover:bg-red-100",
    icon: Ban,
  },
};

const SubscriptionChip: React.FC = () => {
  const navigate = useNavigate();
  const { subscription, loading } = useSubscription();

  if (loading || !subscription) return null;

  const state = getSubscriptionState(subscription);
  if (!state.severity) return null;

  const style = STYLES[state.severity];
  const Icon = style.icon;

  return (
    <button
      type="button"
      onClick={() => navigate(state.chipTo)}
      title={`${state.chipLabel} — view billing details`}
      className={`hidden sm:inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${style.chip}`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="whitespace-nowrap">{state.chipLabel}</span>
    </button>
  );
};

export default SubscriptionChip;
